import { recordKey, upcomingEvents, type CaseGraph, type PropertyCase } from './cases';
import type { ComplianceDashboard, ComplianceItem } from './compliance-items';
import { isPermit, type DisplayRecord } from './property-records';
import { publicWorksDetails } from './public-works-display';
import { documentTime } from './record-dates';
import { latestPurchase } from './property-feed';
import type { PropertySummary } from './types';
import { taxBillInstallments, type TaxBill } from '../skills/sf-treasurer/scripts/treasurer';
import type { TimelineFilter } from './source-presentation';
import { rentBoardTimelinePresentation } from '../skills/sf-rent-board/scripts/timeline';

const GROUP_RANK = { overdue: 0, due: 1, open: 2 } as const;

const ATTENTION_RANK: Record<ComplianceItem['attention'], number> = {
  action: 0,
  progress: 1,
  corrected: 2,
};

export type OpenItem = ComplianceItem & { group: keyof typeof GROUP_RANK };

/** A row of the brief's case list: a case, or a queue item outside any case. */
type BriefRow =
  | { kind: 'case'; key: string; item: PropertyCase; queue?: OpenItem }
  | { kind: 'item'; key: string; queue: OpenItem };

/**
 * Open queue items ranked by urgency: city findings first, then the owner's
 * permits, then 311 reports, nearest deadline first within each.
 */
function rankedQueue(dashboard: ComplianceDashboard) {
  const kindRank = (item: ComplianceItem) =>
    !item.record ? 0 : item.record.kind === '311' ? 2 : isPermit(item.record.kind) ? 1 : 0;
  return (
    dashboard.items
      // A decision settles a row for the brief exactly as for the Compliance tab.
      // A date still on the calendar is not due yet, as the Compliance tab folds it.
      .filter(
        (item): item is OpenItem =>
          item.group in GROUP_RANK &&
          item.bucket !== 'calendar' &&
          !(item.decision?.status === 'done' && item.decision.state !== 'disputed'),
      )
      .sort(
        (a, b) =>
          GROUP_RANK[a.group] - GROUP_RANK[b.group] ||
          ATTENTION_RANK[a.attention] - ATTENTION_RANK[b.attention] ||
          kindRank(a) - kindRank(b) ||
          (a.daysUntilDue ?? Number.MAX_SAFE_INTEGER) - (b.daysUntilDue ?? Number.MAX_SAFE_INTEGER),
      )
  );
}

/**
 * One row per open case, carrying its most urgent queue item, followed by
 * queue items that belong to no case (a lien, a tax bill). The queue and the
 * brief are the same derivation; this is the case-shaped view of it.
 */
function briefRows(openCases: PropertyCase[], queue: OpenItem[], graph: CaseGraph) {
  const byCase = new Map<string, OpenItem>();
  const loose: OpenItem[] = [];
  for (const item of queue) {
    const caseKey = item.record && graph.caseByRecord.get(recordKey(item.record));
    if (!caseKey) loose.push(item);
    else if (!byCase.has(caseKey)) byCase.set(caseKey, item);
  }
  const order = (row: BriefRow) =>
    row.queue
      ? GROUP_RANK[row.queue.group] * 3 + ATTENTION_RANK[row.queue.attention]
      : Number.MAX_SAFE_INTEGER;
  const rows: BriefRow[] = [
    ...openCases.map((item): BriefRow => ({
      kind: 'case',
      key: item.key,
      item,
      queue: byCase.get(item.key),
    })),
    ...loose.map((queue): BriefRow => ({
      kind: 'item',
      key: queue.key,
      queue,
    })),
  ];
  return rows.sort(
    (a, b) =>
      order(a) - order(b) ||
      (a.kind === 'case' && b.kind === 'case'
        ? b.item.lastActivityTime - a.item.lastActivityTime
        : 0),
  );
}

const NON_ACTIVITY_KINDS = new Set<string>(['street-view']);

export function propertyBriefModel(props: {
  graph: CaseGraph;
  records: DisplayRecord[];
  dashboard: ComplianceDashboard;
  now: number;
  loading: boolean;
  timelineEvents?: import('./runtime-contract').TimelineEvent[];
  /** The property's local time zone; see property-time.ts. */
  timeZone?: string | null;
}) {
  const byKey = new Map(props.records.map((record) => [recordKey(record), record]));
  // A case counts as open only while one of its records is in the queue, so a
  // decade-old hearing referral the queue aged out is not an open case here.
  const queued = new Set(
    props.dashboard.items
      .filter((item) => item.group !== 'closed')
      .flatMap((item) => item.members ?? [item])
      .flatMap((item) => (item.record ? [recordKey(item.record)] : [])),
  );
  const openCases = props.graph.cases.filter(
    (item) => item.status === 'open' && item.memberKeys.some((key) => queued.has(key)),
  );
  const enforcement = openCases.filter((item) => item.category === 'enforcement');
  const openPermits = props.records.filter((record) => isPermit(record.kind) && !record.archived);
  const upcoming = upcomingEvents(props.graph, props.now, props.timeZone);
  // Approved street or utility work whose window is ahead or underway. It is
  // published by block, so it is context for the reader, never an owner item.
  const streetWork = props.records
    .flatMap((record) =>
      record.kind === 'public-works'
        ? [{ record, details: publicWorksDetails(record, props.now) }]
        : [],
    )
    .filter(({ details }) => details.attention)
    .sort(
      (a, b) =>
        (a.details.start ?? 0) - (b.details.start ?? 0) ||
        (a.details.end ?? 0) - (b.details.end ?? 0),
    );
  // Imagery capture dates are not city activity, even though each capture is
  // its own single-record case.
  const lastActivity = [
    ...props.graph.cases
      .filter((item) => !NON_ACTIVITY_KINDS.has(byKey.get(item.anchorKey)?.kind ?? ''))
      .map((item) => item.lastActivityTime),
    ...(props.timelineEvents ?? []).map((event) => documentTime(event.date) ?? 0),
  ]
    .filter((time) => time > 0 && time <= props.now)
    .sort((a, b) => b - a)[0];
  const queue = rankedQueue(props.dashboard);
  const urgent = queue.some((item) => item.group !== 'open');
  const rows = briefRows(openCases, queue, props.graph);
  const calm = !props.loading && queue.length === 0 && upcoming.length === 0 && rows.length === 0;
  const quiet = calm && streetWork.length === 0;

  return {
    byKey,
    enforcement,
    openPermits,
    upcoming,
    streetWork,
    lastActivity,
    queue,
    urgent,
    rows,
    calm,
    quiet,
  };
}

export function recordUpdatedAt(record: DisplayRecord) {
  return record.activity?.lastActivityAt || record.date;
}

export function recordOpenedAt(record: DisplayRecord) {
  const opened = record.openedAt || record.date;
  return opened && opened !== recordUpdatedAt(record) ? opened : null;
}

type FeedGroup<T> = {
  items: T[];
  year: number | null;
  showYear: boolean;
  collapsed: boolean;
};

/** Runs of more than two past street permits within a year fold into one row. */
export function groupPastPublicWorks<T extends { record: DisplayRecord; time: number | null }>(
  list: T[],
  now: number,
) {
  const groups: FeedGroup<T>[] = [];
  let lastYear: number | null = null;
  let run: FeedGroup<T>[] = [];
  const flushRun = () => {
    if (run.length > 2) {
      groups.push({
        ...run[0]!,
        items: run.map((entry) => entry.items[0]!),
        collapsed: true,
      });
    } else {
      groups.push(...run);
    }
    run = [];
  };
  for (const item of list) {
    const year = item.time === null ? null : new Date(item.time).getUTCFullYear();
    const showYear = year !== null && year !== lastYear;
    lastYear = year ?? lastYear;
    const entry = { items: [item], year, showYear, collapsed: false };
    const past =
      item.record.kind === 'public-works' && publicWorksDetails(item.record, now).compact;
    if (!past || showYear) flushRun();
    if (past) run.push(entry);
    else groups.push(entry);
  }
  flushRun();
  return groups;
}

export function timelineView(
  summary: PropertySummary,
  records: DisplayRecord[],
  filter: TimelineFilter,
) {
  const purchase = latestPurchase(summary);
  const purchaseTime = documentTime(purchase?.documentDate);
  const items = records.map((record) => {
    const updated = recordUpdatedAt(record);
    const time = documentTime(updated);
    const isPurchase =
      record.kind === 'recorder' && purchase !== null && record.id === purchase.documentNumber;
    const beforePurchase =
      purchaseTime !== null &&
      time !== null &&
      (time < purchaseTime || (time === purchaseTime && !isPurchase));
    return { record, updated, time, isPurchase, beforePurchase };
  });
  const current = items.filter((item) => !item.beforePurchase);
  const older = items.filter((item) => item.beforePurchase);
  const filtering = filter !== 'all';
  const showPurchase = filter === 'all' || filter === 'recorder';
  return { items, current, older, filtering, showPurchase };
}

/** Earliest unpaid installment across bills; `delinquentAfter` is the portal's MM/DD/YYYY. */
export function nextUnpaid(bills: TaxBill[]) {
  const due = bills.flatMap((bill) =>
    taxBillInstallments(bill)
      .filter((installment) => installment.status === 'unpaid')
      .map((installment) => ({ bill, installment })),
  );
  const time = (value: string | null) => {
    const match = value && /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    return match
      ? Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2]))
      : Number.POSITIVE_INFINITY;
  };
  return due.sort(
    (a, b) => time(a.installment.delinquentAfter) - time(b.installment.delinquentAfter),
  )[0];
}

/** One ordered feed for legacy records and skill-authored events; never invent dates. */
export function propertyTimelineGroups(
  summary: PropertySummary,
  records: DisplayRecord[],
  filter: TimelineFilter,
  now: number,
) {
  const view = timelineView(summary, records, filter);
  type Legacy = (typeof view.items)[number];
  type Event = import('./runtime-contract').TimelineEvent;
  type Entry =
    | { kind: 'record'; value: Legacy; time: number | null; key: string; beforePurchase: boolean }
    | { kind: 'event'; value: Event; time: number; key: string; beforePurchase: boolean };
  const purchase = documentTime(latestPurchase(summary)?.documentDate);
  const entries: Entry[] = view.items.map((value) => ({
    kind: 'record',
    value,
    time: value.time,
    key: `record:${value.record.kind}:${value.record.id}`,
    beforePurchase: value.beforePurchase,
  }));
  if (filter === 'all')
    for (const storedEvent of summary.runtime?.timelineEvents ?? []) {
      const event = rentBoardTimelinePresentation(storedEvent);
      const time = documentTime(event.date);
      if (time === null) continue;
      entries.push({
        kind: 'event',
        value: event,
        time,
        key: `event:${event.source}:${event.id}`,
        beforePurchase: purchase !== null && time < purchase,
      });
    }
  // Within the same instant, keep declared batches together even when record IDs interleave.
  const batchOrder = (entry: Entry) =>
    entry.kind === 'event' && entry.value.batch
      ? JSON.stringify([entry.value.batch.key, entry.value.batch.title, entry.value.dateLabel])
      : '';
  entries.sort(
    (a, b) =>
      (b.time ?? -Infinity) - (a.time ?? -Infinity) ||
      (a.kind === 'event' && b.kind === 'event' && a.value.source === b.value.source
        ? batchOrder(a).localeCompare(batchOrder(b))
        : 0) ||
      a.key.localeCompare(b.key),
  );
  type Group =
    | {
        kind: 'event';
        event: Event;
        events: Event[];
        key: string;
        year: number | null;
        showYear: boolean;
        beforePurchase: boolean;
      }
    | {
        kind: 'records';
        items: Legacy[];
        collapsed: boolean;
        key: string;
        year: number | null;
        showYear: boolean;
        beforePurchase: boolean;
      };
  const group = (items: Entry[]): Group[] => {
    const output: Group[] = [];
    let lastYear: number | null = null;
    for (let i = 0; i < items.length;) {
      const entry = items[i]!;
      if (entry.kind === 'event') {
        const year = new Date(entry.time).getUTCFullYear();
        const events = [entry.value];
        i++;
        if (entry.value.batch) {
          while (i < items.length) {
            const next = items[i]!;
            if (
              next.kind !== 'event' ||
              next.time !== entry.time ||
              next.value.source !== entry.value.source ||
              next.value.dateLabel !== entry.value.dateLabel ||
              next.value.batch?.key !== entry.value.batch.key ||
              next.value.batch.title !== entry.value.batch.title
            )
              break;
            events.push(next.value);
            i++;
          }
        }
        output.push({
          kind: 'event',
          event: entry.value,
          events,
          key: entry.key,
          year,
          showYear: year !== lastYear,
          beforePurchase: entry.beforePurchase,
        });
        lastYear = year;
      } else {
        const run: Legacy[] = [];
        while (i < items.length && items[i]!.kind === 'record') {
          run.push((items[i] as Extract<Entry, { kind: 'record' }>).value);
          i++;
        }
        for (const segment of groupPastPublicWorks(run, now)) {
          const first = segment.items[0]!;
          output.push({
            kind: 'records',
            items: segment.items,
            collapsed: segment.collapsed,
            key: `record:${first.record.kind}:${first.record.id}`,
            year: segment.year,
            showYear: segment.year !== null && segment.year !== lastYear,
            beforePurchase: first.beforePurchase,
          });
          if (segment.year !== null) lastYear = segment.year;
        }
      }
    }
    return output;
  };
  const current = view.filtering ? entries : entries.filter((entry) => !entry.beforePurchase);
  const older = view.filtering ? [] : entries.filter((entry) => entry.beforePurchase);
  return {
    groups: group(current),
    olderGroups: group(older),
    olderCount: older.length,
    filtering: view.filtering,
    showPurchase: view.showPurchase,
  };
}
